// import { Box, Typography } from "@mui/material";

import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { StyledTableCell, StyledTableRow } from "Pages/AdminList/ProfessionalUserList";
import React, { useEffect, useState } from "react";

import { Chip, keyframes, styled } from "@mui/material";
import ColorData from "Components/CstmTheme/ColorData";
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch } from "react-redux";
import { setAlertData, startLoading, stopLoading } from "../../Redux/Screen/Action";
import { StatusChip } from "Pages/OrderHistory/OrderHistory";
import PaymentApi from "Service/Payment.api";
import AdminApi from "Service/Admin.api";
import { formatDate, getStatusColorHex } from "Utils/Common";
import { HeaderCard, SearchCard, StyledContainer } from "Pages/AdminList/ProfessionalUserList";
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HistoryIcon from '@mui/icons-material/History';
import { Fade, Slide, Grid, IconButton, Tooltip } from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import CaseApi from "Service/Case.api";
const pulse = keyframes`
0% { transform: scale(1); }
50% { transform: scale(1.05); }
100% { transform: scale(1); }
`;


interface PaymentHistoryInterface {
  pkPayment: number,
  amount: number,
  currency: string,
  paymentDate: string,
  paymentStatus: string,
  transactionId: string,
  paymentGateway: string,
  paymentType: number,
  caseTypeId: number,
  caseTypeName: string,
  orderTypeId: number,
  orderTypeName: string,
  caseId: number,
  isRefundable: boolean
  clientName: string,
  clientEmail: string,
  clientPhone: string,
  caseNumber: string,
}
interface PaymentHistoryProps {
  hideHeader?: boolean;
}

const PaymentsHistory: React.FC<PaymentHistoryProps> = ({ hideHeader }) => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryInterface[] | null>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [refundReason, setRefundReason] = useState("");

  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [paymentToRefund, setPaymentToRefund] = useState<PaymentHistoryInterface | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const dispatch = useDispatch();
  useEffect(() => {
    fetchPaymentHistory(page, rowsPerPage, searchQuery);
  }, [page, rowsPerPage])

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      fetchPaymentHistory(0, rowsPerPage, searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery])

  // No longer needed for server-side filtering
  const filteredHistory = paymentHistory;
  const fetchPaymentHistory = async (currentPage: number, pageSize: number, query: string = "") => {
    dispatch(startLoading({ loading: true, loadtext: "" }));
    const persistedData = localStorage.getItem("persist:root");

    if (persistedData) {
      try {
        const parsedRoot = JSON.parse(persistedData);
        const userObj = JSON.parse(parsedRoot.user);
        const userId = userObj?.myInfo?.id;
        const userRole = userObj?.myInfo?.userType;
        setUserRole(userRole);

        if (!userId || !userRole) {
          console.error("User ID or Role not found in persisted state.");
          dispatch(stopLoading());
          return;
        }

        let response;
        if (userRole === "ADMIN" || userRole === "SUPERADMIN") {
          response = await PaymentApi.getAdminPaymentHistory(currentPage + 1, pageSize, query);
        } else {
          response = await PaymentApi.getUserPaymentHistory(currentPage + 1, pageSize, query);
        }

        if (response.success && response.data) {
          setPaymentHistory(response.data.data || []);
          setTotalCount(response.data.totalCount || 0);
        } else {
          setPaymentHistory([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error("Failed to fetch payment history:", error);
        setPaymentHistory([]);
        setTotalCount(0);
      } finally {
        dispatch(stopLoading());
      }
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  // Update the existing handleRefund function
  const handleRefund = (payment: PaymentHistoryInterface) => {
    setPaymentToRefund(payment);
    setIsRefundModalOpen(true);
  };


  const initiateRefundApi = async () => {
    if (!paymentToRefund) return;

    // 1. Start Loading
    dispatch(startLoading({ loading: true, loadtext: "Processing refund..." }));

    try {
      if (!refundReason.trim()) {
        dispatch(stopLoading());
        dispatch(setAlertData(
          {
            alertMessage: "Please add a reason for the refund.",
            alertVariant: "error",
            openAlert: true
          }
        ))
        return;
      }

      const refundPayload = {
        caseId: paymentToRefund.caseId,
        transactionId: paymentToRefund.transactionId,
        refundReason: refundReason,
        paymentId: paymentToRefund.pkPayment.toString(),
        amount: paymentToRefund.amount,
      };

      const response = await AdminApi.RefundRequest(refundPayload);
      dispatch(setAlertData(
        {
          alertMessage: "Refund processed successfully for Payment ID: " + paymentToRefund.pkPayment,
          alertVariant: "success",
          openAlert: true
        }
      ));


      fetchPaymentHistory(page, rowsPerPage, searchQuery);
      setIsRefundModalOpen(false);
      setPaymentToRefund(null);

    } catch (error) {
      console.error("Refund failed:", error);
      dispatch(stopLoading());
      dispatch(setAlertData(
        {
          alertMessage: "Refund failed. Please check the network logs and server response.",
          alertVariant: "error",
          openAlert: true
        }
      ))
    } finally {
      dispatch(stopLoading());
    }
  };

  const handleDownloadInvoice = async (caseId: number, paymentId?: number) => {
    dispatch(startLoading({ loading: true, loadtext: "Downloading Invoice..." }));
    try {
      const response = await CaseApi.downloadInvoice({ caseId, paymentId });
      if (response.success && response.data) {
        // Since excel: true is used in CaseApi, response.data should be a blob
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Invoice_${caseId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        // Error is already handled by commonFetch via setAlertData
        console.error("Invoice download failed or returned no data");
      }
    } catch (error) {
      console.error("Invoice download failed:", error);
      dispatch(setAlertData({
        alertMessage: "Failed to download invoice.",
        alertVariant: "error",
        openAlert: true
      }));
    } finally {
      dispatch(stopLoading());
    }
  };

  const renderTable = () => (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
        mb: 10
      }}
    >
      <TableContainer sx={{ overflow: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableCell>Payment ID</StyledTableCell>
              <StyledTableCell>Case ID</StyledTableCell>
              <StyledTableCell>Transaction ID</StyledTableCell>
              <StyledTableCell>Case Type</StyledTableCell>
              <StyledTableCell>Order Type</StyledTableCell>
              {/* show if role is admin and super admin */}
              {(userRole === "ADMIN" || userRole === "SUPERADMIN") && (
                <StyledTableCell>Client Details</StyledTableCell>
              )}
              <StyledTableCell>Amount</StyledTableCell>
              <StyledTableCell>Payment Type</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Created At</StyledTableCell>
              <StyledTableCell>Invoice</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredHistory?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                  <Typography variant="h6">No Payments found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              Array.isArray(filteredHistory) &&
              filteredHistory?.map((payment) => (
                <StyledTableRow key={payment.pkPayment}>
                  <TableCell>{payment.pkPayment}</TableCell>
                  <TableCell>{payment.caseNumber ? payment.caseNumber : payment.caseId}</TableCell>
                  <TableCell>{payment.transactionId}</TableCell>
                  <TableCell>{payment.caseTypeName}</TableCell>
                  <TableCell>{payment.orderTypeName}</TableCell>

                  {/* show if role is admin and super admin */}
                  {(userRole === "ADMIN" || userRole === "SUPERADMIN") && (
                    <TableCell>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {payment.clientName}
                        </Typography>
                        <Typography variant="caption" color="text.primary">
                          {payment.clientEmail}
                        </Typography>
                        <Typography variant="caption" color="text.primary">
                          {payment.clientPhone}
                        </Typography>
                      </div>
                    </TableCell>
                  )}

                  <TableCell>{payment.amount}</TableCell>
                  <TableCell>
                    <StatusChip
                      label={payment.paymentType === 1 ? "Charge" : "Refund"}
                      sx={{
                        background: getStatusColorHex(payment.paymentType === 1 ? "Charge" : "Refund").bg,
                        color: getStatusColorHex(payment.paymentType === 1 ? "Charge" : "Refund").text,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      label={payment.paymentStatus}
                      status={payment.paymentStatus}
                      sx={{
                        background: getStatusColorHex(payment.paymentStatus).bg,
                        color: getStatusColorHex(payment.paymentStatus).text,
                      }}
                    />
                  </TableCell>
                  <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {userRole === "ADMIN" &&
                        payment.paymentType === 1 &&
                        payment.isRefundable === true && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => handleRefund(payment)}
                            disabled={
                              !(payment.paymentStatus === "Success" ||
                                payment.paymentStatus === "PaymentCompleted")
                            }
                          >
                            Request Refund
                          </Button>
                        )}
                      {(payment.paymentStatus === "Success" || payment.paymentStatus === "PaymentCompleted") && (
                        <Tooltip title="Download Invoice">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleDownloadInvoice(payment.caseId, payment.pkPayment)}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {(paymentHistory && paymentHistory.length > 0) && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Paper>
  );

  const renderCards = () => (
    <Box display="flex" flexDirection="column" gap={2} sx={{ mb: 10 }}>
      {paymentHistory?.length === 0 ? (
        <Typography variant="body1" align="center">
          No data found
        </Typography>
      ) : (
        Array.isArray(paymentHistory) &&
        paymentHistory?.map((payment) => (
          <Card key={payment.pkPayment} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Payment ID: {payment.pkPayment}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Case No: {payment.caseNumber ? payment.caseNumber : payment.caseId}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Transaction ID {payment.transactionId}
              </Typography>
              <Typography variant="body2">
                <strong>Case Type:</strong> {payment.caseTypeName}
              </Typography>
              <Typography variant="body2">
                <strong>Order Type:</strong> {payment.orderTypeName}
              </Typography>
              {/* show if role is admin and super admin */}
              {(userRole === "ADMIN" || userRole === "SUPERADMIN") && (
                <Typography variant="body2">
                  <strong>Client Details:</strong>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {payment.clientName}
                    </Typography>
                    <Typography variant="subtitle2">
                      {payment.clientEmail}
                    </Typography>
                    <Typography variant="subtitle2">
                      {payment.clientPhone}
                    </Typography>
                  </div>
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Amount:</strong> {payment.amount}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {payment.paymentStatus}
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {formatDate(payment.paymentDate)}
              </Typography>
              <Box mt={2} display="flex" gap={1}>
                {userRole === "ADMIN" && payment.paymentType === 1 && payment.isRefundable === true && (
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => handleRefund(payment)}
                    // Disable if not 'Success' or 'PaymentCompleted'
                    disabled={!(payment.paymentStatus === 'Success' || payment.paymentStatus === "PaymentCompleted")}
                  >
                    Request Refund
                  </Button>
                )}
                {(payment.paymentStatus === "Success" || payment.paymentStatus === "PaymentCompleted") && (
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadInvoice(payment.caseId, payment.pkPayment)}
                  >
                    Invoice
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        ))
      )}
      {(paymentHistory && paymentHistory.length > 0) && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Box>
  );

  return (
    <StyledContainer maxWidth="xl">
      <Fade in timeout={800}>
        <Box sx={{ width: '100%', pb: 10 }}>
          {/* Header Section */}
          {!hideHeader && (
            <HeaderCard elevation={8} sx={{ mb: 4 }}>
              <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexDirection={{ xs: 'column', md: 'row' }}
                  gap={2}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <ReceiptLongIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56 } }} />
                    <Box>
                      <Typography
                        variant="h4"
                        component="h1"
                        fontWeight="bold"
                        color="white"
                        gutterBottom
                      >
                        Payment History
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ opacity: 0.9 }}
                        color="white"
                      >
                        View and manage all financial transactions and payment records
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </HeaderCard>
          )}

          {/* Search Section */}
          <SearchCard elevation={2} sx={{ mb: 4 }}>
            <CardContent sx={{ p: '20px !important' }}>
              <TextField
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by Payment ID, Case ID, Transaction ID or Client details..."
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="primary" />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
              />
            </CardContent>
          </SearchCard>

          <Slide direction="up" in timeout={600}>
            <Box>
              {isMobile ? renderCards() : renderTable()}
            </Box>
          </Slide>
        </Box>
      </Fade>

      {/* Refund Confirmation Modal */}
      <Dialog
        open={isRefundModalOpen}
        onClose={() => {
          setIsRefundModalOpen(false);
          setRefundReason("");
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: theme.palette.error.main, color: 'white', fontWeight: 'bold' }}>
          Confirm Refund Processing
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText sx={{ color: "text.primary", mb: 3 }}>
            You are about to initiate a refund for <strong>₹{paymentToRefund?.amount}</strong>.
            Please provide a valid reason for this refund request.
          </DialogContentText>

          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            label="Reason for Refund"
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            placeholder="Detailed explanation for the refund..."
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
              '& .Mui-focused fieldset': { borderColor: theme.palette.primary.main }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => {
              setIsRefundModalOpen(false);
              setRefundReason("");
            }}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => initiateRefundApi()}
            variant="contained"
            color="error"
            disabled={!refundReason.trim()}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Yes, Process Refund
          </Button>
        </DialogActions>
      </Dialog>
    </StyledContainer>
  );
};



export default PaymentsHistory;